import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { TeamMemberExcelFailure } from '../../types/entity.types';
import { SagaPayload } from '../api.types';
import {
  DownloadTeamExcelRequest,
  DownloadTeamExcelResponse,
  UploadTeamExcelRequest,
  UploadTeamExcelResponse,
  ViewTeamLeaderboardRequest,
  ViewTeamLeaderboardResponse,
  ViewTeamsForProgrammeRequest,
  ViewTeamsForProgrammeResponse
} from './programme-teams.types';

import {
  DOWNLOADING_TEAM_EXCEL,
  RETRIEVING_TEAMS,
  RETRIEVING_TEAM_LEADERBOARD,
  UPLOADING_TEAM_EXCEL
} from './programme-teams.constants';

import {
  requestDownloadTeamExcel,
  requestUploadTeamExcel,
  requestViewTeamLeaderboard,
  requestViewTeamsForProgramme
} from './programme-teams.actions';

import {
  getDownloadTeamExcelRequest,
  getUploadTeamExcelRequest,
  getViewTeamLeaderboardRequest,
  getViewTeamsForProgrammeRequest
} from './programme-teams.endpoints';

import { setTeamLeaderboard } from '../../slices/programme/accountability/accountability.slice';
import { setTeams } from '../../slices/programme/team/team.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';
import { downloadExcelFile } from '../../utils/util';

export function* performViewTeamsForProgrammeRequest({ payload }: SagaPayload<ViewTeamsForProgrammeRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TEAMS));

    const [endpoint, options] = getViewTeamsForProgrammeRequest(requestData);

    const { data }: AxiosResponse<ViewTeamsForProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(setTeams(data.teams));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TEAMS));
  }
}

export function* watchForViewTeamsForProgrammeRequest() {
  yield takeLatest(requestViewTeamsForProgramme.type, performViewTeamsForProgrammeRequest);
}

export function* performDownloadTeamExcelRequest({ payload }: SagaPayload<DownloadTeamExcelRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_TEAM_EXCEL));

    const [endpoint, options] = getDownloadTeamExcelRequest(requestData);

    const { data }: AxiosResponse<DownloadTeamExcelResponse> = yield call(axios, endpoint, options);

    yield call(downloadExcelFile, data.data, data.documentName);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_TEAM_EXCEL));
  }
}

export function* watchForDownloadTeamExcelRequest() {
  yield takeLatest(requestDownloadTeamExcel.type, performDownloadTeamExcelRequest);
}

export function* performUploadTeamExcelRequest({
  payload
}: SagaPayload<UploadTeamExcelRequest, undefined, TeamMemberExcelFailure[]>) {
  const { requestData, onError } = payload;

  try {
    yield put(addSpinner(UPLOADING_TEAM_EXCEL));

    const request = getUploadTeamExcelRequest(requestData);

    const { data }: AxiosResponse<UploadTeamExcelResponse> = yield call(axios, ...request);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    yield put(
      requestViewTeamsForProgramme({
        requestData: { programmeExternalReference: requestData.programmeExternalReference }
      })
    );
  } catch (error) {
    const results = (error as { response?: { data?: { results?: unknown } } })?.response?.data
      ?.results as TeamMemberExcelFailure[];

    if (results && onError) {
      yield call(onError, results);
    } else {
      const message = getErrorMessage(error);
      yield put(addSystemNotice({ type: ERROR, message }));
    }
  } finally {
    yield put(removeSpinner(UPLOADING_TEAM_EXCEL));
  }
}

export function* watchForUploadTeamExcelRequest() {
  yield takeLatest(requestUploadTeamExcel.type, performUploadTeamExcelRequest);
}

export function* performViewTeamLeaderboardRequest({ payload }: SagaPayload<ViewTeamLeaderboardRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TEAM_LEADERBOARD));

    const request = getViewTeamLeaderboardRequest(requestData);

    const { data }: AxiosResponse<ViewTeamLeaderboardResponse> = yield call(axios, ...request);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    const teamleaderboardData = {
      ...data,
      message: undefined
    };
    yield put(setTeamLeaderboard(teamleaderboardData));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TEAM_LEADERBOARD));
  }
}

export function* watchForViewTeamLeaderboardRequest() {
  yield takeLatest(requestViewTeamLeaderboard.type, performViewTeamLeaderboardRequest);
}

export default function* programmeTeamsSaga() {
  yield all([
    watchForViewTeamsForProgrammeRequest(),
    watchForDownloadTeamExcelRequest(),
    watchForUploadTeamExcelRequest(),
    watchForViewTeamLeaderboardRequest()
  ]);
}
